
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { useUserRoles } from "@/hooks/useUserRoles";

interface UseApplicationsProps {
  teamId: string | undefined;
  isValidUUID: boolean;
}

export const useApplications = ({ teamId, isValidUUID }: UseApplicationsProps) => {
  const { toast } = useToast();
  const { user } = useSupabaseUser();

  return useQuery({
    queryKey: ['applications', teamId, user?.id],
    queryFn: async () => {
      if (!isValidUUID || !user || !teamId) {
        toast({
          title: "Invalid Team ID",
          description: "Please select a team",
          variant: "destructive",
        });
        return [];
      }

      // With RLS policies in place, we can just query the applications table
      // The policies will automatically filter results based on user's role and team membership
      const { data, error } = await supabase
        .from('applications')
        .select('*')
        .eq('team_id', teamId)
        .order('submitted_at', { ascending: false });

      if (error) {
        toast({
          title: "Error loading applications",
          description: error.message,
          variant: "destructive",
        });
        console.error("Error fetching applications:", error);
        throw error;
      }

      return data;
    },
    enabled: isValidUUID && Boolean(user),
  });
};
